import {normalizeDegreeAudit,type DegreeAudit,DEGREE_REQUIREMENTS,addedClassRequirement,normalizeDegreeEntry,type DegreeEntry} from '../domain/degreeProgress';
/**
 * Every read the interface makes.
 *
 * Components call hooks from this file and nothing else — there is no
 * `getDocs` anywhere in `src/routes` or `src/components`. That is what makes
 * it possible to reason about read volume, to change a collection name in one
 * place, and to apply visibility consistently.
 *
 * Visibility deserves a note, because it is the one thing here that is not
 * optional. Firestore evaluates security rules *per returned document* on a
 * list query, and a single document that fails the rule fails the entire
 * query — not silently, with a permission error. So a student's query cannot
 * merely be filtered afterwards; it has to be narrowed up front to exactly
 * what the rules will allow, or the screen shows an error instead of content.
 *
 * Hence `constrainToPublished`. Admin queries are unconstrained (they may have
 * the drafts), student queries carry `state == 'published'`, and while
 * previewing as a student Admin gets the student query — which is what makes
 * the preview honest rather than a simulation.
 */

import { useMemo, useSyncExternalStore } from 'react';
import { activeCourseIds } from '../../shared/courseVisibility';
import { useCourseSubscriptions } from './courseSubscriptions';
import {
  collectionGroup,
  collection,
  doc,
  limit as fbLimit,
  orderBy,
  query,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';

import { useCollection, useDocument, queryKey, type AsyncData } from './firestoreHooks';
import * as P from './paths';
import * as N from './normalize';
import { useSession } from '../state/SessionProvider';
import type {
  AgendaEvent,
  Announcement,
  AppUser,
  Course,
  CourseDocument,
  DeliveryRecord,
  DocumentCategory,
  Enrollment,
  InboxItem,
  NotificationGroup,
  PushSubscriptionRecord,
  SeenRecord,
  Semester,
  StudentActivityEntry,
  Todo,
} from '../domain/types';
import { isLiveForStudents, semesterInSession } from '../../shared/authz';

// ── Helpers ──────────────────────────────────────────────────────────────────

function mapAll<T>(
  convert: (snap: QueryDocumentSnapshot<DocumentData>) => T
): (snapshot: QuerySnapshot<DocumentData>) => T[] {
  return (snapshot) => snapshot.docs.map((d) => convert(d));
}

/**
 * The current reader's answer to "may I see unpublished things?".
 *
 * Threaded through every content hook rather than consulted inside them, so a
 * screen that wants the student answer while Admin is signed in — the preview —
 * gets it without a second code path.
 */
export function useSeesDrafts(): boolean {
  const { effectiveRole } = useSession();
  return effectiveRole === 'admin';
}

/** Filter a list to what the effective role may actually see. */
function visible<T extends { state: 'draft' | 'published'; publishAt?: Date | null }>(
  items: T[],
  seesDrafts: boolean,
  now: Date
): T[] {
  if (seesDrafts) return items;
  return items.filter((item) => isLiveForStudents(item, now));
}

/**
 * The constraints a student's query must carry.
 *
 * Empty for Admin. For everyone else this mirrors the `isLive()` helper in
 * firestore.rules exactly — same field, same comparison — because the rules
 * are evaluated against every document the query returns, and any mismatch
 * turns a screen into a permission error rather than a shorter list.
 */
function constrainToPublished(seesDrafts: boolean, options: { publishAt?: Date } = {}): QueryConstraint[] {
  if (seesDrafts) return [];
  const constraints: QueryConstraint[] = [where('state', '==', 'published')];
  // Announcements also carry a publish time, and the rules check it. Anything
  // dated in the future must be excluded here too or it fails the whole query.
  if (options.publishAt) constraints.push(where('publishAt', '<=', options.publishAt));
  return constraints;
}

// ── Semesters ────────────────────────────────────────────────────────────────

/**
 * Every semester. Admin only.
 *
 * `enabled` is not an optimisation. This lists the collection unconstrained,
 * and the rules evaluate every returned document — so for a student it fails
 * on the first semester they are not enrolled in and returns
 * permission-denied for the whole query. It must not be attached at all
 * unless the caller is Admin.
 */
export function useAllSemesters(enabled = true): AsyncData<Semester[]> {
  return useCollection<Semester>(
    () => (enabled ? query(P.semestersCol(), orderBy('startAt', 'desc')) : null),
    mapAll(N.toSemester),
    queryKey('semesters:all', enabled)
  );
}

/**
 * The semesters the signed-in reader belongs to.
 *
 * A collection-group query over enrollments, not a scan of the semester list —
 * students cannot read that list at all. One query returns the memberships,
 * and only those semesters are then fetched.
 */
export function useMyEnrollments(): AsyncData<Array<Enrollment & { semesterId: string }>> {
  const { user } = useSession();
  const uid = user?.uid ?? null;

  return useCollection<Enrollment & { semesterId: string }>(
    () => (uid ? query(P.allEnrollments(), where('uid', '==', uid)) : null),
    (snapshot) =>
      snapshot.docs.map((d) => ({
        ...N.toEnrollment(d),
        // .../semesters/{sid}/enrollments/{uid}
        semesterId: d.ref.parent.parent?.id ?? '',
      })),
    queryKey('enrollments:mine', uid)
  );
}

export function useSemester(semesterId: string | undefined): AsyncData<Semester | null> {
  return useDocument<Semester>(
    () => (semesterId ? P.semesterDoc(semesterId) : null),
    (snap) => N.toSemester(snap),
    queryKey('semester', semesterId)
  );
}

/**
 * The semesters to show on the home screen.
 *
 * Admin gets everything, newest first, and the interface sorts them into
 * current, upcoming and past. A student gets only semesters they are enrolled
 * in *and* that are in session — before the start date a semester is not
 * "upcoming", it is absent, which is why this returns nothing rather than
 * something disabled.
 */
/**
 * The semesters to show on the home screen — Admin's view.
 *
 * Students do not use this: see useEnrolledSemesterIds and the note there
 * about why their semesters have to be read one at a time.
 */
export function useMySemesters(now: Date = new Date()): AsyncData<Semester[]> {
  const { actualRole, viewAsStudent } = useSession();
  const admin = actualRole === 'admin';

  const all = useAllSemesters(admin);

  return useMemo(() => {
    if (!admin) return { data: [], loading: false, error: null, stale: false };
    // Previewing: apply the date window a student is held to, so a semester
    // that has not started is absent rather than merely badged.
    if (!viewAsStudent) return all;
    return {
      ...all,
      data: all.data.filter((s) => semesterInSession({ startAt: s.startAt, endAt: s.endAt, now })),
    };
    // `now` is deliberately not a dependency: it changes every render and the
    // window it decides is measured in months.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, viewAsStudent, all]);
}

/**
 * The semester ids a student is enrolled in.
 *
 * Why this is not a single query for the semesters themselves: Firestore
 * evaluates security rules against *every document a list query returns*, and
 * one refusal fails the entire query rather than omitting that row. A student
 * enrolled in a term that has not started yet — the normal case while a
 * semester is being prepared — would therefore get `permission-denied` for
 * their whole home screen instead of an empty one.
 *
 * So the ids come from here, and each semester is then read individually by
 * its own card. A refused `get` hides one semester; it cannot break the page.
 */
export function useEnrolledSemesterIds(): {
  ids: string[];
  loading: boolean;
  error: AsyncData<unknown>['error'];
} {
  const enrollments = useMyEnrollments();
  const ids = useMemo(
    () => enrollments.data.map((e) => e.semesterId).filter(Boolean),
    [enrollments.data]
  );
  /*
   * The error is passed through deliberately.
   *
   * Swallowing it makes a failed query indistinguishable from "enrolled in
   * nothing" — both render as an empty screen, and a student reporting "my
   * semester is missing" gives nobody anything to work with. A refused or
   * mis-indexed query has to say so.
   */
  return { ids, loading: enrollments.loading, error: enrollments.error };
}

export function useEnrollments(semesterId: string | undefined): AsyncData<Enrollment[]> {
  return useCollection<Enrollment>(
    () => (semesterId ? query(P.enrollmentsCol(semesterId), orderBy('email')) : null),
    mapAll(N.toEnrollment),
    queryKey('enrollments', semesterId)
  );
}

/** Enumerate dates, then fetch only the documents the reader can actually open. */
export function useActiveCourseIds(semesterId: string | undefined) {
  const admin=useSeesDrafts(), now=useNow();
  const calendar=useDocument<Record<string,unknown>>(() => semesterId&&!admin ? doc(P.semesterDoc(semesterId),'viewer','courseCalendar') : null,
    snap=>snap.data()??{},queryKey('course-calendar',semesterId,admin));
  const semester=useSemester(!admin?semesterId:undefined);
  const raw=(calendar.data?.courses??{}) as Record<string,{state:string;startAt?:unknown;endAt?:unknown}>;
  const windows=Object.fromEntries(Object.entries(raw).map(([id,data])=>[id,{state:data.state,startAt:N.toDate(data.startAt),endAt:N.toDate(data.endAt)}]));
  return {inSession:!!semester.data&&semesterInSession({...semester.data,now}),ids:semester.data?activeCourseIds(windows,semester.data,now):[],loading:!admin&&(calendar.loading||semester.loading),error:calendar.error||semester.error};
}

function useScopedContent<T extends {id:string;courseId:string|null;state:'draft'|'published';publishAt?:Date|null}>(
  semesterId:string|undefined,kind:string,convert:(snapshot:QueryDocumentSnapshot<DocumentData>)=>T,publishedAt?:Date
):AsyncData<T[]> {
  const admin=useSeesDrafts(), active=useActiveCourseIds(semesterId), now=useNow();
  const result=useCourseSubscriptions<T>(()=>!admin&&semesterId&&active.inSession&&!active.loading&&!active.error
    ? [null,...active.ids].map(id=>query(collection(P.semesterDoc(semesterId),kind),where('courseId','==',id),
      ...constrainToPublished(false,{publishAt:publishedAt}))) : [],convert,
    queryKey('scoped',semesterId,kind,admin,active.inSession,active.loading,active.error?.code,active.ids.join(','),publishedAt?.getTime()));
  return {...result,loading:!admin&&(active.loading||result.loading),error:active.error||result.error,
    data:result.data.filter(item=>active.inSession&&isLiveForStudents(item,now)&&(!item.courseId||active.ids.includes(item.courseId)))};
}

function useScopedDocument<T extends {courseId:string|null;state:'draft'|'published';publishAt?:Date|null}>(result:AsyncData<T|null>,semesterId:string|undefined):AsyncData<T|null> {
  const admin=useSeesDrafts(),active=useActiveCourseIds(semesterId),now=useNow();
  if(admin)return result;
  return {...result,loading:result.loading||active.loading,
    data:active.inSession&&result.data&&isLiveForStudents(result.data,now)&&(!result.data.courseId||active.ids.includes(result.data.courseId))?result.data:null};
}

// ── Courses ──────────────────────────────────────────────────────────────────

export function useCourses(semesterId: string | undefined): AsyncData<Course[]> {
  const seesDrafts = useSeesDrafts();
  const now = useNow();

  const result = useCollection<Course>(
    () =>
      semesterId && seesDrafts
        ? query(P.coursesCol(semesterId), ...constrainToPublished(seesDrafts), orderBy('order'))
        : null,
    mapAll(N.toCourse(semesterId ?? '')),
    queryKey('courses', semesterId, seesDrafts)
  );

  const active=useActiveCourseIds(semesterId);
  const student=useCourseSubscriptions<Course>(()=>!seesDrafts&&semesterId&&!active.loading&&!active.error
    ? active.ids.map(id=>P.courseDoc(semesterId,id)) : [],N.toCourse(semesterId??''),
    queryKey('active-courses',semesterId,seesDrafts,active.loading,active.ids.join(',')));
  const filtered=useFiltered(result,seesDrafts,now);
  return seesDrafts?filtered:{...student,loading:active.loading||student.loading,error:active.error||student.error,
    data:student.data.filter(c=>active.ids.includes(c.id)).sort((a,b)=>a.order-b.order)};
}

export function useCourse(
  semesterId: string | undefined,
  courseId: string | undefined
): AsyncData<Course | null> {
  const admin=useSeesDrafts(), active=useActiveCourseIds(semesterId);
  const result=useDocument<Course>(
    () => (semesterId && courseId && (admin || active.ids.includes(courseId??'')) ? P.courseDoc(semesterId, courseId) : null),
    (snap) => N.toCourse(semesterId ?? '')(snap),
    queryKey('course', semesterId, courseId, admin, active.ids.includes(courseId??''))
  );
  return admin?result:{...result,loading:active.loading||result.loading,data:courseId&&active.ids.includes(courseId)?result.data:null};
}

// ── Announcements ────────────────────────────────────────────────────────────

export function useAnnouncements(
  semesterId: string | undefined,
  options: { courseId?: string | null; max?: number } = {}
): AsyncData<Announcement[]> {
  const seesDrafts = useSeesDrafts();
  const now = useNow();
  const { courseId = null, max } = options;

  const result = useCollection<Announcement>(
    () => {
      if (!semesterId || !seesDrafts) return null;
      const constraints: QueryConstraint[] = [
        ...constrainToPublished(seesDrafts, { publishAt: now }),
        orderBy('publishAt', 'desc'),
      ];
      if (max) constraints.push(fbLimit(max));
      return query(P.announcementsCol(semesterId), ...constraints);
    },
    mapAll(N.toAnnouncement(semesterId ?? '')),
    // `now` is rounded to the minute, so this resubscribes at most once a
    // minute — which is also how quickly a scheduled post becomes visible.
    queryKey('announcements', semesterId, max, seesDrafts, now.getTime())
  );

  const student=useScopedContent(semesterId,'announcements',N.toAnnouncement(semesterId??''),now);
  const combined=seesDrafts?result:{...student,data:[...student.data].sort((a,b)=>(b.publishAt?.getTime()??0)-(a.publishAt?.getTime()??0)).slice(0,max??Infinity)};
  const filtered = useFiltered(combined, seesDrafts, now);

  return useMemo(() => {
    if (!courseId) return filtered;
    return { ...filtered, data: filtered.data.filter((a) => a.courseId === courseId) };
  }, [filtered, courseId]);
}

export function useAnnouncement(
  semesterId: string | undefined,
  announcementId: string | undefined
): AsyncData<Announcement | null> {
  const result=useDocument<Announcement>(
    () => (semesterId && announcementId ? P.announcementDoc(semesterId, announcementId) : null),
    (snap) => N.toAnnouncement(semesterId ?? '')(snap),
    queryKey('announcement', semesterId, announcementId)
  );
  return useScopedDocument(result,semesterId);
}

// ── Coursework ───────────────────────────────────────────────────────────────

export function useTodos(
  semesterId: string | undefined,
  options: { courseId?: string | null } = {}
): AsyncData<Todo[]> {
  const seesDrafts = useSeesDrafts();
  const now = useNow();
  const { courseId = null } = options;

  const result = useCollection<Todo>(
    () =>
      semesterId && seesDrafts
        ? query(P.todosCol(semesterId), ...constrainToPublished(seesDrafts), orderBy('dueAt', 'asc'))
        : null,
    mapAll(N.toTodo(semesterId ?? '')),
    queryKey('todos', semesterId, seesDrafts)
  );

  const student=useScopedContent(semesterId,'todos',N.toTodo(semesterId??''));
  const combined=seesDrafts?result:{...student,data:[...student.data].sort((a,b)=>(a.dueAt?.getTime()??0)-(b.dueAt?.getTime()??0))};
  const filtered = useFiltered(combined, seesDrafts, now);

  return useMemo(() => {
    if (!courseId) return filtered;
    return { ...filtered, data: filtered.data.filter((t) => t.courseId === courseId) };
  }, [filtered, courseId]);
}

export function useTodo(
  semesterId: string | undefined,
  todoId: string | undefined
): AsyncData<Todo | null> {
  const result=useDocument<Todo>(
    () => (semesterId && todoId ? P.todoDoc(semesterId, todoId) : null),
    (snap) => N.toTodo(semesterId ?? '')(snap),
    queryKey('todo', semesterId, todoId)
  );
  return useScopedDocument(result,semesterId);
}

// ── Documents ────────────────────────────────────────────────────────────────

export function useDocuments(semesterId: string | undefined): AsyncData<CourseDocument[]> {
  const seesDrafts = useSeesDrafts();
  const now = useNow();

  const result = useCollection<CourseDocument>(
    () =>
      semesterId && seesDrafts
        ? query(
            P.documentsCol(semesterId),
            ...constrainToPublished(seesDrafts),
            orderBy('createdAt', 'desc')
          )
        : null,
    mapAll(N.toCourseDocument(semesterId ?? '')),
    queryKey('documents', semesterId, seesDrafts)
  );

  const student=useScopedContent(semesterId,'documents',N.toCourseDocument(semesterId??''));
  const combined=seesDrafts?result:{...student,data:[...student.data].sort((a,b)=>(b.createdAt?.getTime()??0)-(a.createdAt?.getTime()??0))};
  return useFiltered(combined,seesDrafts,now);
}

export function useDocumentItem(
  semesterId: string | undefined,
  documentId: string | undefined
): AsyncData<CourseDocument | null> {
  const result=useDocument<CourseDocument>(
    () => (semesterId && documentId ? P.documentDoc(semesterId, documentId) : null),
    (snap) => N.toCourseDocument(semesterId ?? '')(snap),
    queryKey('document', semesterId, documentId)
  );
  return useScopedDocument(result,semesterId);
}

export function useDocumentCategories(semesterId: string | undefined): AsyncData<DocumentCategory[]> {
  return useCollection<DocumentCategory>(
    () => (semesterId ? query(P.documentCategoriesCol(semesterId), orderBy('order')) : null),
    mapAll(N.toDocumentCategory(semesterId ?? '')),
    queryKey('docCategories', semesterId)
  );
}

// ── Agenda ───────────────────────────────────────────────────────────────────

export function useAgenda(semesterId: string | undefined): AsyncData<AgendaEvent[]> {
  const seesDrafts = useSeesDrafts();
  const now = useNow();

  const result = useCollection<AgendaEvent>(
    () =>
      semesterId && seesDrafts
        ? query(
            P.agendaCol(semesterId),
            ...constrainToPublished(seesDrafts),
            orderBy('startAt', 'asc')
          )
        : null,
    mapAll(N.toAgendaEvent(semesterId ?? '')),
    queryKey('agenda', semesterId, seesDrafts)
  );

  const student=useScopedContent(semesterId,'agenda',N.toAgendaEvent(semesterId??''));
  const combined=seesDrafts?result:{...student,data:[...student.data].sort((a,b)=>a.startAt.getTime()-b.startAt.getTime())};
  return useFiltered(combined,seesDrafts,now);
}

// ── Bulletin ─────────────────────────────────────────────────────────────────

export function useInbox(max = 60): AsyncData<InboxItem[]> {
  const { user } = useSession();
  const uid = user?.uid ?? null;

  return useCollection<InboxItem>(
    () => (uid ? query(P.inboxCol(uid), orderBy('createdAt', 'desc'), fbLimit(max)) : null),
    mapAll(N.toInboxItem),
    queryKey('inbox', uid, max)
  );
}

/**
 * The unread badge.
 *
 * Reads the same subscription the bulletin does, so opening the feed and
 * marking something read updates the badge without a second round trip. Capped
 * because a count is a count — nobody needs to know it is exactly 214.
 */
export function useUnreadCount(): number {
  const { data } = useInbox(60);
  return useMemo(() => data.filter((item) => !item.readAt).length, [data]);
}

// ── Admin ────────────────────────────────────────────────────────────────────

export function useDirectory(): AsyncData<AppUser[]> {
  return useCollection<AppUser>(
    () => query(P.usersCol(), orderBy('email')),
    mapAll(N.toUser),
    'users:all'
  );
}

export function useNotificationGroups(): AsyncData<NotificationGroup[]> {
  return useCollection<NotificationGroup>(
    () => query(P.notificationGroupsCol(), orderBy('name')),
    mapAll(N.toNotificationGroup),
    'groups:all'
  );
}

export function useDeliveries(
  filters: { status?: string; channel?: string; max?: number } = {}
): AsyncData<DeliveryRecord[]> {
  const { status, channel, max = 200 } = filters;

  const result = useCollection<DeliveryRecord>(
    () => query(P.deliveriesCol(), orderBy('createdAt', 'desc'), fbLimit(max)),
    mapAll(N.toDelivery),
    queryKey('deliveries', max)
  );

  /*
   * Filtering happens here rather than in the query.
   *
   * Every combination of status and channel would otherwise need its own
   * composite index, and the working set — the most recent few hundred
   * deliveries — is small enough that the client can slice it instantly. The
   * `max` cap is what keeps that true.
   */
  return useMemo(() => {
    let rows = result.data;
    if (status && status !== 'all') rows = rows.filter((d) => d.status === status);
    if (channel && channel !== 'all') rows = rows.filter((d) => d.channel === channel);
    return { ...result, data: rows };
  }, [result, status, channel]);
}

export function usePushSubscriptions(): AsyncData<PushSubscriptionRecord[]> {
  return useCollection<PushSubscriptionRecord>(
    () => query(P.pushSubscriptionsCol(), orderBy('lastSeenAt', 'desc')),
    mapAll(N.toPushSubscription),
    'push:all'
  );
}

export function useStudentActivity(max = 200): AsyncData<StudentActivityEntry[]> {
  return useCollection<StudentActivityEntry>(
    () => query(P.studentActivityCol(), orderBy('at', 'desc'), fbLimit(max)),
    mapAll(N.toStudentActivity),
    queryKey('activity', max)
  );
}

/** Who has seen what, for one semester. Admin only, and invisible to students. */
export function useSeenRecords(semesterId: string | undefined): AsyncData<SeenRecord[]> {
  return useCollection<SeenRecord>(
    () => (semesterId ? query(P.seenCol(semesterId)) : null),
    mapAll(N.toSeenRecord),
    queryKey('seen', semesterId)
  );
}

// ── Internals ────────────────────────────────────────────────────────────────

/**
 * A clock that is stable across renders but not frozen for the session.
 *
 * Visibility decisions compare against "now", and using `new Date()` inline
 * would make every render a new dependency. Rounding to the minute gives
 * memoisation something to hold onto while keeping a scheduled publish from
 * being more than 60 seconds late to appear.
 */
const clockListeners=new Set<()=>void>();
let clockTimer:ReturnType<typeof setInterval>|null=null;
const clockSnapshot=()=>Math.floor(Date.now()/60000)*60000;
function subscribeClock(listener:()=>void) {
  clockListeners.add(listener);
  if(!clockTimer)clockTimer=setInterval(()=>clockListeners.forEach(fn=>fn()),1000);
  return ()=>{clockListeners.delete(listener);if(!clockListeners.size&&clockTimer){clearInterval(clockTimer);clockTimer=null;}};
}
export function useNow(): Date {
  const minute=useSyncExternalStore(subscribeClock,clockSnapshot,clockSnapshot);
  return useMemo(()=>new Date(minute),[minute]);
}

function useFiltered<T extends { state: 'draft' | 'published'; publishAt?: Date | null }>(
  result: AsyncData<T[]>,
  seesDrafts: boolean,
  now: Date
): AsyncData<T[]> {
  return useMemo(
    () => ({ ...result, data: visible(result.data, seesDrafts, now) }),
    [result, seesDrafts, now]
  );
}

/** Saved globally after a week of genuine class activity; admin only. */
export function useProfessors(): AsyncData<{id: string; name: string}[]> {
  const { effectiveRole } = useSession();
  return useCollection(() => effectiveRole === 'admin' ? query(P.professorsCol(), orderBy('name')) : null,
    snapshot => snapshot.docs.map(d => ({id: d.id, name: String(d.data().name ?? '')})),
    queryKey('professors', effectiveRole));
}

export function useDegreeEntries(): AsyncData<{id:string;entry:DegreeEntry}[]> {
  const {user,effectiveRole}=useSession();
  const uid=effectiveRole==='admin'?user?.uid:null;
  return useCollection(()=>uid?query(P.degreeEntriesCol(uid)):null,
    snapshot=>snapshot.docs.flatMap(d=>{const req=DEGREE_REQUIREMENTS.find(r=>r.id===d.id)??(d.id.startsWith('custom-')?addedClassRequirement(d.id):null);return req?[{id:d.id,entry:normalizeDegreeEntry(d.data(),req)}]:[];}),
    queryKey('degreeProgress',uid));
}

export function useDegreeCourseHistory() {
 const {effectiveRole}=useSession();
 return useCollection<Course>(()=>effectiveRole==='admin'?query(collectionGroup(P.usersCol().firestore,'courses')):null,
 snapshot=>snapshot.docs.filter(d=>d.ref.parent.parent?.parent.id==='semesters').map(d=>N.toCourse(d.ref.parent.parent!.id)(d)),queryKey('degree-course-history',effectiveRole));
}

export function useDegreeAudit():AsyncData<DegreeAudit|null> {
 const {user,effectiveRole}=useSession();const uid=effectiveRole==='admin'?user?.uid:null;
 return useDocument(()=>uid?P.degreeEntryDoc(uid,'audit-summary'):null,snap=>snap.exists()?normalizeDegreeAudit(snap.data()):null,queryKey('degree-audit',uid));
}
